import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import type { BlogPostSummary } from '@inafana/shared';
import { api } from '@/lib/api';
import { usePageMeta } from '../usePageMeta';

export function formatPostDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Skeleton(): React.JSX.Element {
  return (
    <ul className="post-list" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <li key={i} className="post-row is-loading">
          <div className="post-date">
            <span className="sk sk-sm" />
          </div>
          <div className="post-main">
            <span className="sk sk-lg" />
            <span className="sk sk-md" />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function BlogIndex(): React.JSX.Element {
  usePageMeta(
    'Notes on agentic AI — Inafana',
    'What we have learned building agentic AI systems, RAG applications, and the software they run on — written from production, not from demos.',
  );

  const { data, isPending, isError } = useQuery({
    queryKey: ['blog', 'list'],
    queryFn: () => api<{ posts: BlogPostSummary[] }>('/blog'),
  });

  const posts = data?.posts ?? [];

  return (
    <>
      <section className="blog-hero">
        <div className="blob blob-blue" style={{ width: 520, height: 520, top: -180, left: '-8%' }} />
        <div className="blob blob-violet" style={{ width: 400, height: 400, top: -60, right: '-4%' }} />
        <div className="container">
          <div className="blog-hero-copy">
            <h1 className="h1 reveal">
              Notes from <span className="grad">production</span>.
            </h1>
            <p className="lede reveal" data-delay="1">
              What we have actually learned building agentic AI and the software it runs
              on — the parts that broke, the parts that held, and what we would do
              differently. No demos, no predictions.
            </p>
          </div>
        </div>
      </section>

      <section className="blog-list-section">
        <div className="container">
          {isPending && <Skeleton />}

          {isError && (
            <div className="blog-empty">
              <h2 className="h3">These notes could not be loaded</h2>
              <p>
                Something went wrong on our side, not yours. Refresh the page, or{' '}
                <a href="mailto:contact@inafana.com">email us</a> if it keeps happening.
              </p>
            </div>
          )}

          {!isPending && !isError && posts.length === 0 && (
            <div className="blog-empty">
              <h2 className="h3">Nothing published yet</h2>
              <p>
                The first notes are being written. In the meantime, tell us what you are
                building and we will answer directly.
              </p>
              <Link to="/#contact" className="btn btn-primary">
                Start a project <span className="arrow">→</span>
              </Link>
            </div>
          )}

          {posts.length > 0 && (
            <ul className="post-list">
              {posts.map((post, i) => (
                <li key={post.id} className="post-row reveal" data-delay={(i % 3) + 1}>
                  <Link to={`/blog/${post.slug}`} className="post-link">
                    <div className="post-date">{formatPostDate(post.publishedAt)}</div>
                    <div className="post-main">
                      <h2 className="post-title">{post.title}</h2>
                      <p className="post-excerpt">{post.excerpt}</p>
                    </div>
                    <span className="post-go" aria-hidden="true">
                      <ArrowRight size={18} strokeWidth={2} />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </>
  );
}
